"use client";

import React, {
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  forwardRef,
} from "react";
import {
  Autocomplete,
  CircularProgress,
  Chip,
  Checkbox,
  ListItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { debounce } from "lodash";
import { CustomTextField } from "../formsComponents";
import { api } from "../../api/api";
import { useBillingCompanyContext } from "../../providers";
import { MuiTextFieldProps } from "@mui/lab/internal/pickers/PureDateInput";
import { IOpportunities } from "../../redux/opportunites/opportunites.types";
import { EnrAutoComplete } from "../basic/EnrAutoComplete";
import { HIGHLIGHT_STRONG_COLORS } from "../../constants/appColors";

export interface IVenueAutoSearchProps {
  label?: string;
  value: string[];
  disabled?: boolean;
  error?: string | boolean;
  helperText?: string;
  fullWidth?: boolean;
  multiple?: boolean;
  onSelect: (venue: string[]) => void;
  /** When true, applies a yellow highlight border to indicate filter is active */
  isHighlighted?: boolean;
  sx?: MuiTextFieldProps["sx"];
}

export const VenueMultipleAutoSearch = forwardRef<
  {
    clear: () => void;
  },
  IVenueAutoSearchProps
>((props, ref) => {
  const {
    value = [],
    disabled,
    label,
    onSelect,
    error,
    helperText,
    fullWidth = true,
    multiple = true,
    isHighlighted = false,
    sx,
  } = props;

  const [options, setOptions] = useState<readonly IOpportunities[]>([]);
  const [loading, setLoading] = useState(false);
  const companyOutlet = useBillingCompanyContext();
  const [search, setSearchText] = useState<string>("");
  const [inputValue, setInputValue] = useState<string>("");
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const initialLoad = useRef(false);
  const [key, setKey] = React.useState(0);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [dialogSearch, setDialogSearch] = useState<string>("");
  const [dialogOptions, setDialogOptions] = useState<readonly IOpportunities[]>(
    [],
  );
  const [dialogLoading, setDialogLoading] = useState(false);
  const fetchSuggestion = async (enteredValue: string = "") => {
    setLoading(true);
    try {
      let searchQuery = "";
      if (enteredValue.length > 0) {
        searchQuery += `column=venue_name&value=${enteredValue}&`;
      }
      const company_outlet_uuid = companyOutlet.comapny.company_outlet_uuid;
      const url = `/venue/get-venue?${searchQuery}pageNo=1&itemPerPage=100&status=ACTIVE&company_outlet_uuid=${company_outlet_uuid}`;

      const res = await api.get(url);
      let data: IOpportunities[] = res.data.data || [];

      const uniqueData = data.filter(
        (v, i, a) => a.findIndex((t) => t.venue_name === v.venue_name) === i,
      );

      setOptions(uniqueData);
    } catch (error) {
      console.error("Error fetching venue:", error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  // Stable debounced fetcher tied to the current outlet (avoids per-render
  // allocation + stale closure of useCallback(debounce(...), [])).
  const debounceFn = useMemo(
    () => debounce(fetchSuggestion, 800),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyOutlet.comapny.company_outlet_uuid],
  );

  useEffect(() => {
    if (search && search !== value.join(", ")) {
      debounceFn(search);
    }
  }, [search, value, debounceFn]);

  useEffect(() => () => debounceFn.cancel(), [debounceFn]);

  const handleFocus = () => {
    if (!initialLoad.current) {
      fetchSuggestion("");
      initialLoad.current = true;
    }
  };

  useEffect(() => {
    if (
      value &&
      value.length > 0 &&
      options.length === 0 &&
      !initialLoad.current
    ) {
      fetchSuggestion("");
      initialLoad.current = true;
    }
  }, []);

  useImperativeHandle(ref, () => ({
    clear: () => {
      setSearchText("");
      setInputValue("");
      setKey((prev) => prev + 1);
      onSelect([]);
    },
  }));

  React.useEffect(() => {
    if (value.length === 0) {
      setSearchText("");
      setInputValue("");
      setKey((prev) => prev + 1);
    } else {
      setInputValue("");
    }
  }, [value]);
  const getAutocompleteValue = () => {
    if (multiple) {
      if (!value || value.length === 0) return [];
      return options.filter((opt) => value.includes(opt.venue_name || ""));
    } else {
      if (!value || value.length === 0) return null;
      return options.find((opt) => opt.venue_name === value[0]) || null;
    }
  };

  const handleChange = (
    venue: any,
    newValue: IOpportunities | IOpportunities[] | null,
  ) => {
    setInputValue("");
    setSearchText("");

    if (multiple) {
      const selectedVenues = (newValue as IOpportunities[]) || [];
      const venueNames = selectedVenues
        .map((venue) => venue.venue_name)
        .filter((name): name is string => !!name);

      onSelect(venueNames);
    } else {
      const selectedVenues = newValue as IOpportunities | null;
      onSelect(
        selectedVenues && selectedVenues.venue_name
          ? [selectedVenues.venue_name]
          : [],
      );
    }
  };

  const fetchDialogOptions = async (enteredValue: string = "") => {
    setDialogLoading(true);
    try {
      let searchQuery = "";
      if (enteredValue.length > 0) {
        searchQuery += `column=venue_name&value=${enteredValue}&`;
      }
      const company_outlet_uuid = companyOutlet.comapny.company_outlet_uuid;
      const url = `/venue/get-venue?${searchQuery}&company_outlet_uuid=${company_outlet_uuid}`;

      const res = await api.get(url);
      let data: IOpportunities[] = res.data.data || [];

      const uniqueData = data.filter(
        (v, i, a) => a.findIndex((t) => t.venue_name === v.venue_name) === i,
      );

      setDialogOptions(uniqueData);
    } catch (error) {
      console.error("Error fetching venues:", error);
      setDialogOptions([]);
    } finally {
      setDialogLoading(false);
    }
  };

  const debouncedDialogSearch = useMemo(
    () => debounce(fetchDialogOptions, 800),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyOutlet.comapny.company_outlet_uuid],
  );

  useEffect(() => {
    if (openDialog) {
      fetchDialogOptions("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDialog]);

  useEffect(() => {
    if (dialogSearch && openDialog) {
      debouncedDialogSearch(dialogSearch);
    }
  }, [dialogSearch, openDialog, debouncedDialogSearch]);

  useEffect(() => () => debouncedDialogSearch.cancel(), [debouncedDialogSearch]);

  const handleDialogOpen = () => {
    setOpenDialog(true);
    setDialogSearch("");
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setDialogSearch("");
  };

  const handleDialogChange = (venue: any, newValue: IOpportunities[]) => {
    const venueNames = newValue
      .map((venue) => venue.venue_name)
      .filter((name): name is string => !!name);

    onSelect(venueNames);
  };

  const getDialogAutocompleteValue = () => {
    if (!value || value.length === 0) return [];
    return dialogOptions.filter((opt) => value.includes(opt.venue_name || ""));
  };

  return (
    <>
      <EnrAutoComplete
        id="venue-auto-search"
        fullWidth={fullWidth}
        key={key}
        multiple={multiple}
        isHighlighted={isHighlighted}
        hasValue={!!(value && value.length > 0)}
        isFocused={isFocused}
        sx={sx}
        // Input (default renderInput handles label/error/loading/shrink)
        label={label}
        error={typeof error === "string" ? error : undefined}
        helperText={helperText}
        loading={loading}
        // Options + tags (shared defaults: checkbox rows + small chips + "+N")
        showCheckbox
        getOptionLabel={(option) => option.venue_name || ""}
        getTagLabel={(option) => option.venue_name || ""}
        onShowMore={handleDialogOpen}
        maxVisibleTags={1}
        isOptionEqualToValue={(option, val) =>
          option.venue_uuid === val.venue_uuid
        }
        filterOptions={(x) => x}
        options={options}
        autoComplete
        value={getAutocompleteValue()}
        disabled={disabled}
        noOptionsText="Type to search"
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={(venue, newInputValue, reason) => {
          if (reason === "input") {
            setInputValue(newInputValue);
            setSearchText(newInputValue);
          } else {
            setInputValue("");
            setSearchText("");
          }
        }}
        onClose={() => {
          setInputValue("");
          setIsFocused(false);
        }}
        onFocus={() => {
          setIsFocused(true);
          handleFocus();
        }}
        onBlur={() => {
          setIsFocused(false);
        }}
      />
      {openDialog && (
        <Dialog
          open={openDialog}
          onClose={handleDialogClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <span>Select Venue</span>
              <IconButton onClick={handleDialogClose} size="small">
                <CloseIcon />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Autocomplete
                multiple
                fullWidth
                // disableCloseOnSelect
                options={dialogOptions}
                value={getDialogAutocompleteValue()}
                onChange={handleDialogChange}
                getOptionLabel={(option) => option.venue_name || ""}
                isOptionEqualToValue={(option, val) =>
                  option.venue_uuid === val.venue_uuid
                }
                filterOptions={(x) => x}
                loading={dialogLoading}
                noOptionsText="Type to search"
                inputValue={dialogSearch}
                sx={{
                  ...sx,
                  ".MuiOutlinedInput-root": {
                    paddingTop: "1.5px",
                    paddingBottom: "1.5px",
                    fontSize: "0.8rem",
                    color: "rgb(38, 38, 38)",
                    width: "100%",
                  },
                }}
                onInputChange={(venue, newInputValue, reason) => {
                  if (reason === "input") {
                    setDialogSearch(newInputValue);
                  } else {
                    setDialogSearch("");
                  }
                }}
                renderOption={(props, option, { selected }) => {
                  const { key, ...optionProps } = props as any;
                  return (
                    <ListItem key={key} {...optionProps}>
                      <Checkbox checked={selected} sx={{ marginRight: 1 }} />
                      {option.venue_name}
                    </ListItem>
                  );
                }}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        {...tagProps}
                        label={option.venue_name}
                        onDelete={tagProps.onDelete}
                      />
                    );
                  })
                }
                renderInput={(params) => {
                  const hasSelectedValues = value && value.length > 0;

                  return (
                    <CustomTextField
                      {...params}
                      label={label}
                      fullWidth={fullWidth}
                      error={!!error}
                      helperText={helperText}
                      highlight={isHighlighted}
                      InputLabelProps={{
                        ...params.InputLabelProps,
                        shrink: hasSelectedValues || isFocused,
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loading && (
                              <CircularProgress color="inherit" size={20} />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  );
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose} variant="contained">
              Done
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
});
